import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useAuth } from "../../context/AuthContext";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { UPDATE_EMPLOYEE } from "../../api/mutation";
import {
  showError,
  showSuccess,
} from "../../components/common/ToastNotification";
import BreadCumb from "../../components/common/Breadcrumb";
import InputsNew from "../../components/common/InputsNew";
import backArrow from "../../assets/task/back_arrow.svg";
import Loader from "../../components/common/Loader";
import { uploadFileDirectly } from "../projects/AddProject";
import plusIconuplad from "../../assets/task/plus_icon_uload.svg";
import imgUploadicon from "../../assets/task/imguploadicon.svg";
import {
  EMPLOYEE_BY_EMPLOYEE_ID,
  GET_ALL_SKILLS,
  GET_ALL_ROLES,
} from "../../api/query";
import InputsDropdownMultipleSearchSelect from "../../components/common/InputsDropdownMultipleSearchSelect";
import closeDowpDowniconimg from "../../assets/task/closedropdown-cicon.svg";
import { format } from "date-fns";
import DateSelector from "../../components/common/DateSelector";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";

interface EditEmployeeConfig {
  firstName: string;
  podName: string;
  lastName: string;
  email: string;
  mobileNo: string;
  dob: string;
  dateOfJoining: string;
  imageurl?: string;
  role?: any;
}

const EditEmployeeSchema = yup.object().shape({
  firstName: yup
    .string()
    .test(
      "not-only-whitespace",
      "Please add a valid First Name",
      (value) => !value || value.trim().length > 0,
    )
    .required("This field is required"),
  lastName: yup
    .string()
    .test(
      "not-only-whitespace",
      "Please add a valid Last Name",
      (value) => !value || value.trim().length > 0,
    )
    .required("This field is required"),
  mobileNo: yup
    .string()
    .required("This field is required")
    .transform((value) => value.replace(/[^0-9]/g, ""))
    .matches(/^\d{10}$/, "Please add a valid Mobile Number"),
  email: yup
    .string()
    .required("This field is required")
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+$/,
      "Please enter a valid Email Address",
    ),
  dob: yup.string().required("This field is required"),
  dateOfJoining: yup.string().required("This field is required"),
  imageurl: yup.string(),
  role: yup.string().required("This field is required"),
  podName: yup.mixed().nullable(),
});

const EditEmployee = () => {
  const now = new Date();
  const navigate = useNavigate();

  const { userInfo, updateUserInfo } = useAuth();
  const employeeID = userInfo?.employee_id || "";
  const organizationID = userInfo?.agencies?.id || "";
  const employeeName = `${userInfo?.first_name} ${userInfo?.last_name}`;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imgUrl, setImgUrl] = useState("");
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null,
  );
  const [skillOptions, setSkillOptions] = useState<
    { key: string; name: string }[]
  >([]);
  const [selectedSkills, setSelectedSkills] = useState<any[]>([]);
  const [roleOptions, setRoleOptions] = useState<
    { key: string; name: string }[]
  >([]);

  // react hook form
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
    control,
    watch
  } = useForm<any>({
    resolver: yupResolver(EditEmployeeSchema),
  });

  const { data: roleData, loading: roleLoading } = useQuery(GET_ALL_ROLES, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (roleData && roleData.get_all_roles?.data?.length > 0) {
      const options = roleData.get_all_roles.data.map((role: any) => ({
        key: role.id,
        name: role.role_name,
      }));
      setRoleOptions(options);
    }
  }, [roleData]);

  //api
  const { data: skillData, loading: skillLoading } = useQuery(GET_ALL_SKILLS, {
    fetchPolicy: "no-cache",
  });

  const {
    loading,
    data: employeeData,
    refetch,
  } = useQuery(EMPLOYEE_BY_EMPLOYEE_ID, {
    variables: {
      employee_id: parseInt(employeeID),
    },
    fetchPolicy: "no-cache",
    skip: !employeeID,
  });

  const [updateEmployee, { loading: updateEmployeeLoading }] = useMutation(
    UPDATE_EMPLOYEE,
    {
      fetchPolicy: "no-cache",
    },
  );

  useEffect(() => {
    if (
      employeeData?.employee_by_employee_id?.status_code === 200 &&
      roleOptions.length > 0
    ) {
      const employee = employeeData.employee_by_employee_id.employees[0];
      const currentRoleName = employee?.employee_roles?.[0];

      const matchedRole = roleOptions.find((r) => r.name === currentRoleName);
      if (matchedRole) {
        setValue("role", matchedRole.key);
      }
    }
  }, [employeeData, roleOptions]);

  useEffect(() => {
    if (skillData && skillData.get_all_skills?.skills?.length > 0) {
      const options = skillData.get_all_skills?.skills?.map((skill: any) => ({
        key: skill.id,
        name: skill?.skill_name,
      }));
      setSkillOptions(options);
    }
  }, [skillData]);

  useEffect(() => {
    if (
      employeeData &&
      employeeData?.employee_by_employee_id?.status_code === 200
    ) {
      const employee = employeeData?.employee_by_employee_id?.employees[0];

      setValue("firstName", employee?.first_name);
      setValue("podName", employee?.pod_name);
      setValue("lastName", employee?.last_name);
      setValue("mobileNo", employee?.contact_no);
      setValue("email", employee?.email_id);
      setValue("dob", employee?.dob || "");
      setValue("dateOfJoining", employee?.date_of_joining || "");

      if (employee?.image_url) {
        setImgUrl(employee?.image_url);
      }

      const skillOptions = employee?.skills?.map((s: any) => ({
        key: s.skill_id,
        name: s?.skill_name,
      }));

      setSelectedSkills(skillOptions);

      // setValue('imageurl', employee?.first_name);
    }
  }, [employeeData]);

  const podNames = watch("podName")

  console.log(podNames,'podNames')

  const handleSelectMembers = (key: any, name: any, type: any) => {
    const allOptions = skillOptions;

    if (key === "all") {
      if (name === true) {
        // Deselect all
        setSelectedSkills([]);
      } else {
        // Select all
        setSelectedSkills(allOptions);
      }
    } else {
      setSelectedSkills((prev) => {
        const exists = prev.some((item) => item.key == key);
        if (exists) {
          // REMOVE if already selected
          return prev.filter((item) => item.key != key);
        } else {
          // ADD if not selected
          return [...prev, { key, name, type }];
        }
      });
    }
    // } else {
    //     const exists = selectedMembers.find((item) => item.key == key);
    //     if (exists) {
    //         setSelectedMembers((prev) => prev.filter((item) => item.key != key));
    //     } else {
    //         setSelectedMembers((prev) => [...prev, { key, name, type }]);
    //     }
    // }
  };

  const handleRemoveMember = (key: string) => {
    setSelectedSkills((prev) => prev.filter((item) => item.key !== key));
  };

  function handleIconClick() {
    fileInputRef.current?.click();
  }

  async function handleFileUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    // if (!["image/jpeg", "image/png"].includes(file.type)) {
    //     setValidationMessage("Only JPG and PNG files are allowed.");
    //     return;
    // }

    setValidationMessage(null);
    setIsUploading(true);

    try {
      const token = localStorage.getItem("authToken")!;
      const url = await uploadFileDirectly(file, file.name, token);
      setImgUrl(url);
      setValue("imageurl", url);
    } catch (err) {
      console.error("Upload failed", err);
    } finally {
      setIsUploading(false);
    }
  }

  async function onSubmit(data: EditEmployeeConfig) {
    const skills = selectedSkills?.map((s: any) => ({
      skill_id: parseInt(s?.key),
      assigned_date: format(now, "yyyy-MM-dd"),
      proficiency_level: "Expert",
    }));

    const contextSkills = selectedSkills?.map((s: any) => ({
      skill_id: parseInt(s?.key),
      assigned_date: format(now, "yyyy-MM-dd"),
      proficiency_level: "Expert",
      skill_name: s?.name,
    }));

    const input = {
      first_name: data.firstName,
      last_name: data.lastName,
      email_id: data.email,
      contact_no: data.mobileNo,
      agency_id: parseInt(organizationID),
      image_url: data.imageurl,
      skills: skills,
      dob: data?.dob,
      date_of_joining: data?.dateOfJoining,
      role_id: parseInt(data.role),
    };

    try {
      const response = await updateEmployee({
        variables: {
          employee_id: parseInt(employeeID),
          input: input,
        },
      });
      if (response.data.update_employee.status_code === 200) {
        showSuccess(response.data.update_employee.message);

        updateUserInfo({
          first_name: data.firstName,
          last_name: data.lastName,
          dob: data.dob,
          date_of_joining: data.dateOfJoining,
          image_url: data.imageurl,
          skills: contextSkills,
        });

        navigate(-1);
      }
      if (response.data.update_employee.status_code === 400) {
        showError(response.data.update_employee.message);
      }
    } catch (error) {
      if (error instanceof ApolloError) {
        showError(error.message);
      }
    }
  }

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        id="cts-respon-p-dv"
        className="w-full  relative  overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4  box-border text-left text-base text-gray font-manrope"
      >
        <div className="flex-1 flex flex-col items-start justify-start gap-6">
          <BreadCumb />
          <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
            <img
              className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
              alt=""
              src={backArrow}
              onClick={() => navigate(-1)}
            />
            <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
              Employee Profile
            </h3>
          </div>
          <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
            <div className="grid grid-cols-4 gap-4 mq991:grid-cols-1 min-w-full">
              <div className="col-span-3 grid">
                <div className="grid grid-cols-3 mq1400:grid-3 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full">
                  <InputsNew
                    contentPlaceholder="First Name"
                    label="First Name*"
                    type="text"
                    register={register("firstName")}
                    errorMessage={errors.firstName}
                  />
                  <InputsNew
                    contentPlaceholder="Last Name"
                    label="Last Name*"
                    type="text"
                    register={register("lastName")}
                    errorMessage={errors.lastName}
                  />
                  <InputsNew
                    label="Email Address*"
                    contentPlaceholder="Email Address"
                    type="text"
                    register={register("email", {
                      onChange: (e) => {
                        e.target.value = e.target.value.toLowerCase(); // Convert to lowercase while typing
                      },
                    })}
                    errorMessage={errors.email}
                    disabled={true}
                  />
                  <InputsNew
                    label="Mobile Number*"
                    contentPlaceholder="Mobile Number"
                    type="number"
                    register={register("mobileNo")}
                    errorMessage={errors.mobileNo}
                    disabled={true}
                  />
                  <Controller
                    name="dob"
                    control={control}
                    render={({ field }) => (
                      <DateSelector
                        label="Date of Birth*"
                        selectedDate={field.value}
                        onChange={(date: any) => {
                          field.onChange(date);
                          setValue("dob", date);
                          trigger("dob");
                        }}
                        error={errors.dob?.message}
                        nothideParsedValue={true}
                      />
                    )}
                  />

                  <Controller
                    name="dateOfJoining"
                    control={control}
                    render={({ field }) => (
                      <DateSelector
                        label="Date of Joining*"
                        selectedDate={field.value}
                        onChange={(date: any) => {
                          field.onChange(date);
                          setValue("dateOfJoining", date);
                          trigger("dateOfJoining");
                        }}
                        error={errors.dateOfJoining?.message}
                        nothideParsedValue={true}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="col-span-1">
                <div className=" rounded flex flex-col items-center justify-start text-left  mq1275:justify-start  w-[120px] h-[140px] ">
                  <label className="self-stretch relative font-semibold dark:text-white pl-2 pb-[-2px]">
                    Photo Upload
                  </label>
                  <div
                    className={`${imgUrl ? "upload_img_box" : ""} rounded  mq1275:bg-white flex flex-col items-start relative left-[20px] justify-end p-2.5 box-border min-w-[120px] h-[140px] mq950:min-w-full text-center agencyinfo-img-blg mq950:p-0`}
                    id="calltype-row "
                  >
                    {isUploading ? (
                      <div className="self-stretch rounded bg-white flex flex-col items-center justify-center py-[10px] px-[9px]  h-[140px] relative left-[-20px]  border-[1px] border-dashed border-neutral-200 ">
                        <div className="self-stretch flex flex-col items-center justify-center gap-[10px]">
                          <div className="text-main-blue">Uploading...</div>
                        </div>
                      </div>
                    ) : imgUrl ? (
                      <div className="bg-white p-2 py-0 shadow-\[0px_1px_2px_rgba\(16\2c _24\2c _40\2c _0\.05\)\] border-surface-border  relative left-[-20px]   inner_upload_img_box onborading-bdr-blg">
                        <img
                          className=" w-[120px] h-[120px]  relative overflow-hidden shrink-0 cursor-pointer imgagency-upload"
                          loading="lazy"
                          alt=""
                          src={imgUrl}
                          onClick={handleIconClick}
                        />
                      </div>
                    ) : (
                      <div className="self-stretch rounded bg-white flex flex-col w-[120px] h-[120px] items-center justify-center py-[9px] px-[9px] border-[1px] border-dashed border-neutral-200">
                        <div className="self-stretch flex flex-col items-center justify-center gap-[10px]">
                          <img
                            className="w-6 h-6 relative overflow-hidden shrink-0 dark-white-img"
                            loading="lazy"
                            alt=""
                            src={imgUploadicon}
                          />
                          <p className="self-stretch relative capitalize my-0">
                            Photo Upload
                          </p>
                          <div
                            className="cursor-pointer rounded plus-task-btn flex flex-row items-center justify-center p-1.5"
                            onClick={handleIconClick}
                          >
                            <img
                              className="h-5 w-5 relative"
                              loading="lazy"
                              alt=""
                              src={plusIconuplad}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileUpload}
                    accept=".jpg,.jpeg,.png"
                    // accept="image/*"
                  />
                  {validationMessage && (
                    <p className="text-red p-2 cts-bg-error my-0 flex items-center gap-[4px] ctcerror-width-blg">
                      <img src={""} alt="" />
                      {validationMessage}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full">
              <div className="grid">
                <InputsDropdownMultipleSearchSelect
                  label="Skills*"
                  placeHolder="Choose"
                  options={skillOptions}
                  onSelect={handleSelectMembers}
                  inputType="checkbox"
                  checkedItems={selectedSkills}
                />

                {selectedSkills?.length > 0 && (
                  <div className="reportmulselect-blg flex-1 w-full cts-flex1-remd contents flex-col items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg min-w-full">
                    <div className="inner-reportmulselect-blg items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg mt-2">
                      {selectedSkills.map((ss: any) => (
                        <div
                          key={ss?.key}
                          className="reportmulselect-blg-dv rounded-radius-md1 overflow-hidden flex flex-row items-center justify-start gap-[8px] inner-item-select-dv border-[1px] border-solid border-surface-border dark:bg-slate-700"
                        >
                          <div className="flex flex-row items-center justify-start">
                            <div className="relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block dark:text-white">
                              {ss?.name}
                            </div>
                          </div>
                          <img
                            className="h-4 w-4 relative overflow-hidden shrink-0 cursor-pointer"
                            loading="lazy"
                            alt="Remove"
                            src={closeDowpDowniconimg}
                            onClick={() => handleRemoveMember(ss.key)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
             <div className="grid">
              {
                podNames?.length == "0" ?    <InputsNew
                contentPlaceholder="POD Name"
                label="POD Name*"
                type="text"
                register={register("podName")}
                errorMessage={errors.podName}
                disabled={true}
              />
              :
              <>
                   <label className="self-stretch relative font-semibold dark:text-white">POD Name*</label>
              <div className="inner-reportmulselect-blg inner-reportmulselect-item items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg mt-2">
               {
                podNames?.map((it:any)=>{
                  return(
                     <div className="reportmulselect-blg-dv rounded-radius-md1 overflow-hidden flex flex-row items-center justify-start gap-[8px] inner-item-select-dv border-[1px] border-solid border-surface-border dark:bg-slate-700">
                  <div className="flex flex-row items-center justify-start">
                    <div className="relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block dark:text-white">
                    {it}
                    </div>
                  </div>
                </div>
                  )
                })
               }
              </div>
              </>
              }      
             </div>
              <div className="grid">
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <SearchableInputsDropdown
                      label="Role*"
                      placeHolder="Select Role"
                      options={roleOptions}
                      onSelect={(key: any) => {
                        field.onChange(key);
                        trigger("role");
                      }}
                      disableDropdown={true}
                      initialValue={
                        roleOptions?.find((it: any) => it.key == field.value)
                          ?.name || ""
                      }
                    />
                  )}
                />
                {errors.role && (
                  <p className="text-red text-xs mt-1">
                    {errors.role.message as string}
                  </p>
                )}
              </div>
            </div>

            <div className="self-stretch flex flex-row items-center justify-end py-0 pr-0 gap-[8px] mq950:box-border mq1425:flex-wrap  mq1425:box-border mq450:box-border">
              <button
                className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
                type="button"
                onClick={() => navigate(-1)}
              >
                <div className="relative text-sm font-body-xs-semi-bold dark:text-white text-center inline-block">
                  Cancel
                </div>
              </button>
              <button
                className={`cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue`}
                type="submit"
              >
                <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">
                  Save
                </div>
              </button>
            </div>
          </div>
        </div>
      </form>
      {(loading || updateEmployeeLoading || skillLoading) && <Loader />}
    </>
  );
};

export default EditEmployee;
